/**
 * A card of detail beside an element, opened by its owner rather than by the
 * pointer, so a canvas can open it when a node or link is hovered or has
 * keyboard focus. Drawn on Radix Tooltip: placed against the element, kept
 * on screen, named as the element's description, and drawn above everything,
 * the canvas's nodes included. It never takes the pointer, so what is under
 * it stays clickable. Like a tooltip, it never carries the only copy of
 * anything: what it shows is also where a click leads.
 */
import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactElement, ReactNode } from 'react';

const Z_INDEX = 60;
const WIDTH = 380;

export interface HoverCardProps {
  open: boolean;
  content: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right' | undefined;
  /** One element that takes a ref; the card is placed against it. */
  children: ReactElement;
}

export function HoverCard({ open, content, side = 'bottom', children }: HoverCardProps) {
  return (
    <RadixTooltip.Provider delayDuration={0}>
      <RadixTooltip.Root open={open}>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            sideOffset={9}
            collisionPadding={11}
            style={{
              zIndex: Z_INDEX,
              boxSizing: 'border-box',
              maxWidth: WIDTH,
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              padding: '11px 13px',
              background: 'var(--surface-card)',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-panel)',
              boxShadow: 'var(--shadow-popover)',
              color: 'var(--text-strong)',
              fontSize: 'var(--text-meta-lg)',
              lineHeight: 1.45,
              animation: 'popin var(--dur-fast) var(--ease-out)',
              pointerEvents: 'none',
            }}
          >
            {content}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
